import React from 'react';

import ModalWrapper from './ModalWrapper';
import Toggler from 'components/Toggler';

const AlertModal = ({ ...props }) => {
  return (
    <ModalWrapper {...props} cancelButton={false} title="Debt Rainbow Help" width={600}>
      <p>
        Hello! Thanks for checking out Debt Rainbow. I've tried to explain the parts that some people might find confusing here. Feel free to reach out to me if you have any other
        questions or issues and I can post them here!
      </p>
      <Toggler title="What Is Debt Rainbow?">
        <p>
          Debt Rainbow was created for me to keep track of my progress paying down different types of debt from past poor decisions (and school?). Originally, I made colorful
          towers on my wall out of colored post-it notes and covered them with boring gray post-its. Each post-it indicated a $500 chunk I needed to pay, and when my balance fell
          below a certain chunk I was able to remove the gray post-it and show color underneath.
        </p>
        <p>
          The goal behind this being I wanted to put as much money towards killing the loans so I could expose all the color underneath. This website has the same goal, but is much
          easier to maintain than a wall of post-it notes, haha.
        </p>
      </Toggler>
      <Toggler title="How It Works">
        <p>
          When you first hit this site, you would have seen some example debts. You can click on the "Debts" section at the top to edit or remove these and add your own
          information. Once all your debts are added, they are then taken and broken up into blocks which are currently a fixed number shown in the actions bar under the header on
          the right side.
        </p>
        <p>
          The colored blocks grow from the bottom upwards as you pay your balance down. Each block has a balance on it and as you pay off your loan and the current balance of your
          loan falls below that number, the block lights up. Your goal is to make an entire column light up. Once your loan balance reaches zero, the column should be completely
          colored in!
        </p>
      </Toggler>
      <Toggler title="Sorting">
        <p>I've added multiple sort options so you can view your debts in the order you wish to pay them down. Here's a brief description of each one:</p>
        <ul>
          <li>
            <strong>Alphabetical</strong> - Sort debts by name from A-Z.
          </li>
          <li>
            <strong>Avalanche</strong> - Sort debts by interest rate, from largest to smallest. This is the most cost effective way to pay down debt. You can read about it below.
          </li>
          <li>
            <strong>Custom Order</strong> - Sort debts by the order you specify. You'll see a button called "REORDER DEBTS" when this option is selected.
          </li>
          <li>
            <strong>Progress - Least</strong> - Sort debts by your current progress, from least paid to most paid.
          </li>
          <li>
            <strong>Progress - Most</strong> - Sort debts by your current progress, from most paid to least paid.
          </li>
          <li>
            <strong>Snowball</strong> - Sort debts by starting balance, from smallest to largest. This is the most common way to pay down debt. You can read about it below.
          </li>
        </ul>
      </Toggler>
      <Toggler title="Increment Type">
        <p>
          This option allows you to see loans on an equal footing (same number of blocks, regardless of balance) or on a more granular scale by letting you set the dollar block
          size per loan.
        </p>
        <ul>
          <li>
            <strong>Equal Blocks</strong> - All loans show an equal number of blocks depending on the "Blocks Count" that you choose.
          </li>
          <li>
            <strong>Dollar Blocks</strong> - Uses the "Dollar Block Size" option on each loan or defaults to $500 blocks. This allows you to break huge loans (Mortgage/Car) into
            more achievable chunks. It also allows you know that paying $500 would definitely fill a new block.
          </li>
        </ul>
      </Toggler>
      <Toggler title="Interest/Payment Buttons">
        <p>
          I've added some helper buttons to quickly add interest/payments. These are strictly adding and subtracting and don't do any automatic increases in balances. This tool was
          meant to be simple and I didn't want to overcomplicate things with automatic interest guesses.
        </p>
        <ul>
          <li>
            <strong>Add Interest</strong> - Increases current balance by x amount{' '}
            <span role="img" aria-label="Sad">
              😭
            </span>
          </li>
          <li>
            <strong>Add Payment</strong> - Decreases current balance by x amount{' '}
            <span role="img" aria-label="Happy">
              😍
            </span>
          </li>
        </ul>
      </Toggler>
      <Toggler title="Avalanche vs Snowball">
        <p>
          You may or may not have heard about the Avalanche and Snowball methods before. These are the two most common ways that people pay down debt, which also saves them more
          money on interest. Both methods rely on "snowballing" your loan payments by using payments from previous loans to pay off the next loan.
        </p>
        <h4>Snowball - Smallest Balance to Largest Balance</h4>
        <p>
          The Snowball method works by taking your loans and ordering them from smallest balance to largest balance. Say you have three loans - <strong>A - $500</strong>,{' '}
          <strong>B - $700</strong>, and <strong>C - $900</strong>. With the <em>Snowball</em> method, you would pay the the minimum balance on all loans, and any extra money you
          have would go to loan <strong>A</strong>. You continue this until loan <strong>A</strong> is paid off.
        </p>
        <p>
          Once loan <strong>A</strong> is paid off, you'd focus on the next lowest balance, which in our case is loan <strong>B</strong>. You would now pay the minimum payments to
          both <strong>B</strong> and <strong>C</strong>, but also take the money you used to pay to loan <strong>A</strong> (minimum, plus extra payments) and apply it to loan{' '}
          <strong>B</strong>. You'd continue this trend until loan <strong>B</strong> is paid off.
        </p>
        <p>
          Now that loan <strong>B</strong> is paid off, you might notice by now how this snowballing works. The money you put towards loan <strong>A</strong> helped you pay off
          loan <strong>B</strong> much faster since you had extra money to apply to it. If we continue this, we can pay the minimum on loan <strong>C</strong> plus also take the
          money we put towards loan <strong>B</strong> (minimum, plus loan <strong>A</strong> minimum, plus extra payments) and apply it to loan <strong>C</strong>. At this point
          we'll be done with loan <strong>C</strong> in no time!
        </p>
        <p>
          At the end of each loan, you'll have a bunch of money freed up to add to the next loan until you have no loans left{' '}
          <span role="img" aria-label="Awesome">
            😎
          </span>
        </p>
        <h4>Avalanche - Largest Interest to Smallest Interest</h4>
        <p>
          Avalanche works very similarly to Snowball, except it actually saves you even more money on interest. You'll do the exact same thing you do with Snowballing, except
          instead of ordering loans from smallest to largest balance, you'll order them from largest to smallest interest rate.
        </p>
        <p>
          By paying highest interest rate first, you'll be cutting down the overall balance that the interest is generated from, which in return saves you money paid to interest.
        </p>
        <h4>Which Do I Choose?</h4>
        <p>
          This is a hot topic and really comes down to how focused you are on paying down debt. Avalanche is the most efficient way to pay off your debts, but it comes at the cost
          of being much slower moving mentally. Snowball is much quicker moving and makes it feel like you're making progress a lot faster (which feels great)!
        </p>
        <p>
          Typically higher balance loans have larger interest rates, so you'll be spending a longer time paying them off, but you'll also be saving money on interest. This is great
          for people with great focus, but can be disheartening to people that like to see progress.
        </p>
        <p>
          The one other benefit to Snowball is that you'll be clearing up cash flow faster. So if there's an emergency and you need extra money, you might have the extra money you
          need by paying off the smaller loans first. However, make sure you don't fall into a trend of wasting the extra cash flow on non-emergencies or you'll be killing your
          snowball effectiveness.
        </p>
        <p>
          For more reading about Snowball vs Avalanche, check out this{' '}
          <a href="https://studentloanhero.com/featured/debt-avalanche-vs-debt-snowball/" target="_blank" rel="noopener noreferrer">
            link
          </a>
          .
        </p>
      </Toggler>
    </ModalWrapper>
  );
};

export default AlertModal;



// WEBPACK FOOTER //
// ./src/components/Modals/HelpModal.js